import { fabric } from "fabric";
// Save As
export async function handleSaveAs(
  canvas,
  format = "PNG",
  dpi = 300,
  setLastFileHandle
) {
  console.log("handleSaveAs");
  try {
    let extension = format.toLowerCase();
    if (extension === "bitmap") extension = "bmp";

    const typeMap = {
      png: { mime: "image/png", ext: ".png", desc: "PNG File" },
      jpeg: { mime: "image/jpeg", ext: ".jpg", desc: "JPEG File" },
      jpg: { mime: "image/jpeg", ext: ".jpg", desc: "JPEG File" },
      tiff: { mime: "image/tiff", ext: ".tiff", desc: "TIFF File" },
      bmp: { mime: "image/bmp", ext: ".bmp", desc: "Bitmap File" },
      svg: { mime: "image/svg+xml", ext: ".svg", desc: "SVG File" },
      pdf: { mime: "application/pdf", ext: ".pdf", desc: "PDF File" },
    };

    const typeDef = typeMap[extension];
    if (!typeDef) throw new Error(`Unsupported format: ${format}`);

    const fileHandle = await window.showSaveFilePicker({
      suggestedName: `canvas${typeDef.ext}`,
      types: [
        {
          description: "Image Files",
          accept: {
            "image/png": [".png"],
            "image/jpeg": [".jpg", ".jpeg"],
            "image/svg+xml": [".svg"],
            "application/pdf": [".pdf"],
            "image/tiff": [".tiff"],
            "image/bmp": [".bmp"],
          },
        },
      ],
    });

    const writable = await fileHandle.createWritable();
    const blob = await getCanvasBlob(canvas, format, dpi);
    await writable.write(blob);
    await writable.close();

    setLastFileHandle?.(fileHandle);
    console.log(`Saved successfully!`);
  } catch (err) {
    console.error("Save As failed:", err);
  }
}

// Save (re-use handle if available)
export async function handleSave(
  canvas,
  lastFileHandle,
  format = "PNG",
  dpi = 96,
  setLastFileHandle
) {
  try {
    console.log("handleSave");
    console.log("lastFileHandle inside handleSave(): ", lastFileHandle);
    if (!lastFileHandle) {
      console.log("No lastFileHandle, invoking Save As");
      return handleSaveAs(canvas, format, dpi, setLastFileHandle);
    }

    const writable = await lastFileHandle.createWritable();
    const blob = await getCanvasBlob(canvas, format, dpi);
    await writable.write(blob);
    await writable.close();

    console.log(`Saved to existing file successfully!`);
  } catch (err) {
    console.error("Save failed:", err);
  }
}

// Updated helper
async function getCanvasBlob(canvas, format, dpi) {
  const scale = dpi / 96;

  // Handle SVG
  if (format === "SVG") {
    if (canvas.toSVG) {
      // Fabric.js way
      return new Blob([canvas.toSVG()], { type: "image/svg+xml" });
    }
    throw new Error("SVG export only supported for Fabric.js canvas");
  }

  // Handle PDF
  if (format === "PDF") {
    const { jsPDF } = await import("jspdf");
    const width = canvas.width || canvas.getWidth?.();
    const height = canvas.height || canvas.getHeight?.();
    const pdf = new jsPDF("p", "pt", [width, height]);

    let dataUrl;
    if (canvas.toDataURL) {
      // Fabric.js
      try {
        dataUrl = canvas.toDataURL({ format: "png", multiplier: scale });
      } catch {
        // raw <canvas>
        dataUrl = canvas.toDataURL("image/png");
      }
    }

    pdf.addImage(dataUrl, "PNG", 0, 0, width, height);
    return pdf.output("blob");
  }

  // Raster (PNG, JPEG, BMP, TIFF)
  if (canvas.toDataURL) {
    let dataUrl;
    try {
      // Fabric.js style
      dataUrl = canvas.toDataURL({
        format: format.toLowerCase(),
        multiplier: scale,
      });
    } catch {
      // fallback for raw <canvas>
      dataUrl = canvas.toDataURL(`image/${format.toLowerCase()}`);
    }
    return await fetch(dataUrl).then((r) => r.blob());
  }

  throw new Error("Invalid canvas object passed to getCanvasBlob");
}

// Open File
export async function handleOpenFile(canvas, setLastFileHandle) {
  try {
    const [fileHandle] = await window.showOpenFilePicker({
      types: [
        {
          description: "Supported Files",
          accept: {
            "application/json": [".json"],
            "image/svg+xml": [".svg"],
            "image/png": [".png"],
            "image/jpeg": [".jpg", ".jpeg"],
            "image/bmp": [".bmp"],
            "image/tiff": [".tiff"],
          },
        },
      ],
    });

    const file = await fileHandle.getFile();
    const contents = await file.text();

    // Reset to A4 size (210 x 297 mm at 96 DPI ≈ 794 x 1123 px)
    canvas.setWidth(794);
    canvas.setHeight(1123);
    canvas.clear();

    // Always add A4 background rectangle
    const background = new fabric.Rect({
      left: 0,
      top: 0,
      width: 794,
      height: 1123,
      fill: "white",
      selectable: false,
      evented: false,
    });
    canvas.add(background);
    canvas.sendToBack(background);

    if (file.name.endsWith(".json")) {
      canvas.loadFromJSON(contents, () => canvas.renderAll());
    } else if (file.name.endsWith(".svg")) {
      fabric.loadSVGFromString(contents, (objects, options) => {
        const obj = fabric.util.groupSVGElements(objects, options);

        const origWidth = obj.width || options.width || 1;
        const origHeight = obj.height || options.height || 1;

        const maxWidth = 794;
        const maxHeight = 1123;

        const scale = Math.min(maxWidth / origWidth, maxHeight / origHeight);
        obj.scale(scale);

        obj.set({
          left: (maxWidth - origWidth * scale) / 2,
          top: (maxHeight - origHeight * scale) / 2,
        });

        canvas.add(obj);
        canvas.renderAll();
      });
    } else {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const fabricImg = new fabric.Image(img, {
          scaleX: 794 / img.width,
          scaleY: 1123 / img.height,
        });
        canvas.add(fabricImg);
        canvas.renderAll();
      };
    }

    setLastFileHandle(fileHandle);
  } catch (err) {
    console.error("Open file failed:", err);
  }
}


export function handlePrint(canvas) {
  if (!canvas) {
    console.error("No canvas to print");
    return;
  }

  // Open new window immediately (so popup blockers don't block it)
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Popup blocked! Please allow popups for this site to print.");
    return;
  }

  // Convert canvas to image (sync step, no await)
  const dataUrl = canvas.toDataURL({
    format: "png",
    multiplier: 2,
  });

  // Write HTML to the print window
  printWindow.document.write(`
    <html>
      <head><title>Print Canvas</title></head>
      <body style="margin:0; text-align:center;">
        <img src="${dataUrl}" style="max-width:100%; height:auto;"/>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          };
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
}